import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Car, AlertCircle, Star, DollarSign, Gift } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import RideTracker from '../components/RideTracker';
import RatingModal from '../components/RatingModal';

const RideTrackingPage = () => {
  const navigate = useNavigate();
  const { rideId } = useParams();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [ride, setRide] = useState(null);
  const [driver, setDriver] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const prevStatusRef = useRef(null);
  
  // Tip state
  const [tipSettings, setTipSettings] = useState(null);
  const [selectedTip, setSelectedTip] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [hasTipped, setHasTipped] = useState(false);
  const [isSubmittingTip, setIsSubmittingTip] = useState(false);
  
  // Fetch tip settings
  useEffect(() => {
    const fetchTipSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/tips/settings`);
        setTipSettings(response.data);
      } catch (err) {
        console.error('Error fetching tip settings:', err);
      }
    };
    fetchTipSettings();
  }, [BACKEND_URL]);

  const fetchRideDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/rides/${rideId}`);
      const newRide = response.data.ride;
      setRide(newRide);
      setDriver(response.data.driver);
      setError(null);
      
      // Check if already rated or tipped
      if (newRide?.rating) {
        setHasRated(true);
      }
      if (newRide?.tip_amount) {
        setHasTipped(true);
      }
      
      // Show rating modal when ride just completed (status changed to completed)
      if (newRide?.status === 'completed' && prevStatusRef.current !== 'completed' && !newRide?.rating) {
        toast({
          title: "Ride Completed!",
          description: "Please rate your driver",
        });
        // Show rating modal after a short delay
        setTimeout(() => setShowRatingModal(true), 1500);
      }
      
      prevStatusRef.current = newRide?.status;
    } catch (error) {
      console.error('Error fetching ride:', error);
      setError('Unable to load ride details');
    } finally {
      setIsLoading(false);
    }
  }, [BACKEND_URL, rideId]);

  useEffect(() => {
    if (rideId) {
      fetchRideDetails();
      
      // Poll for updates every 5 seconds (only if ride is not completed)
      const interval = setInterval(() => {
        if (prevStatusRef.current !== 'completed') {
          fetchRideDetails();
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [rideId, fetchRideDetails]);

  const handleRatingSubmitted = (rating) => {
    setHasRated(true);
    setRide(prev => ({ ...prev, rating }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/user')}>
              <ArrowLeft size={20} />
            </Button>
            <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain" />
            <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">Track Ride</span>
          </div>
        </header>
        
        <div className="container mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Ride Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "We couldn't find details for this ride."}</p>
          <Button onClick={() => navigate('/user')}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        rideId={rideId}
        driverName={driver?.name}
        onRatingSubmitted={handleRatingSubmitted}
      />
      
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/user')}>
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">Track Your Ride</span>
            </div>
          </div>
          {ride.status === 'completed' && (
            <Button 
              onClick={() => navigate('/user')}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
            >
              Book Another Ride
            </Button>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {ride.status === 'completed' ? (
          <Card className="p-8 text-center border-2">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Car className="text-green-600" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Ride Completed!</h2>
            <p className="text-gray-600 mb-4">Thank you for riding with J-Style Services</p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">From</span>
                  <span className="font-medium text-right">{ride.pickup?.address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">To</span>
                  <span className="font-medium text-right">{ride.dropoff?.address}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-gray-600">Total Fare</span>
                  <span className="font-bold text-green-600">${ride.estimated_price}</span>
                </div>
                {(hasTipped || ride.tip_amount > 0) && (
                  <div className="flex justify-between text-green-600">
                    <span>Tip</span>
                    <span className="font-medium">${ride.tip_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                )}
              </div>
            </div>
            
            {driver && (
              <div className="mb-6">
                {/* Driver Info with Photo */}
                <div className="flex items-center justify-center gap-3 mb-3">
                  {driver.profile_photo ? (
                    <img 
                      src={driver.profile_photo.startsWith('http') ? driver.profile_photo : `${BACKEND_URL}${driver.profile_photo}`}
                      alt={driver.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                      data-testid="completed-driver-photo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
                      <Car className="text-white" size={24} />
                    </div>
                  )}
                  <div className="text-sm text-gray-600">
                    Driver: <strong>{driver.name}</strong> • {driver.vehicle_model}
                  </div>
                </div>
                
                {/* Rating Section */}
                {hasRated || ride.rating ? (
                  <div className="flex items-center justify-center gap-1 mb-4">
                    <span className="text-sm text-gray-600 mr-2">Your rating:</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        size={20}
                        className={star <= (ride.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                ) : (
                  <Button 
                    variant="outline"
                    onClick={() => setShowRatingModal(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 mb-4"
                    data-testid="rate-driver-btn"
                  >
                    <Star size={16} className="mr-2" />
                    Rate Your Driver
                  </Button>
                )}
                
                {/* Tip Section */}
                {tipSettings?.enabled && !hasTipped && !ride.tip_amount && (
                  <Card className="p-4 border-2 border-dashed border-green-300 bg-green-50/50 mt-4" data-testid="tip-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift size={18} className="text-green-600" />
                      <span className="font-semibold text-green-800">Add a tip for {driver.name}?</span>
                    </div>
                    
                    {/* Preset Tip Amounts */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {tipSettings.preset_amounts?.map((amount) => (
                        <Button
                          key={amount}
                          variant={selectedTip === amount ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setSelectedTip(amount);
                            setCustomTip('');
                          }}
                          className={selectedTip === amount ? "bg-green-500 hover:bg-green-600" : ""}
                        >
                          ${amount}
                        </Button>
                      ))}
                      {tipSettings.custom_allowed && (
                        <Button
                          variant={selectedTip === 'custom' ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedTip('custom')}
                          className={selectedTip === 'custom' ? "bg-green-500 hover:bg-green-600" : ""}
                        >
                          Custom
                        </Button>
                      )}
                    </div>
                    
                    {/* Custom Tip Input */}
                    {selectedTip === 'custom' && (
                      <div className="flex gap-2 mb-3">
                        <div className="relative flex-1">
                          <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="number"
                            min="1"
                            step="0.5"
                            placeholder="Enter amount"
                            value={customTip}
                            onChange={(e) => setCustomTip(e.target.value)}
                            className="pl-8"
                            data-testid="custom-tip-input"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Submit Tip Button */}
                    {(selectedTip || customTip) && (
                      <Button
                        className="w-full bg-green-500 hover:bg-green-600"
                        onClick={async () => {
                          const tipAmount = selectedTip === 'custom' ? parseFloat(customTip) : selectedTip;
                          if (!tipAmount || tipAmount <= 0) {
                            toast({ title: "Invalid amount", description: "Please enter a valid tip amount", variant: "destructive" });
                            return;
                          }
                          
                          setIsSubmittingTip(true);
                          try {
                            await axios.post(`${BACKEND_URL}/api/users/rides/${rideId}/tip?amount=${tipAmount}`);
                            setHasTipped(true);
                            toast({ title: "Thank you! 🎉", description: `Your $${tipAmount.toFixed(2)} tip has been sent to ${driver.name}` });
                            fetchRideDetails();
                          } catch (err) {
                            toast({ title: "Error", description: "Failed to send tip", variant: "destructive" });
                          } finally {
                            setIsSubmittingTip(false);
                          }
                        }}
                        disabled={isSubmittingTip}
                        data-testid="submit-tip-btn"
                      >
                        {isSubmittingTip ? (
                          <Loader2 size={16} className="animate-spin mr-2" />
                        ) : (
                          <Gift size={16} className="mr-2" />
                        )}
                        Send ${selectedTip === 'custom' ? (parseFloat(customTip) || 0).toFixed(2) : selectedTip?.toFixed(2)} Tip
                      </Button>
                    )}
                    
                    <p className="text-xs text-center text-gray-500 mt-2">100% of tips go directly to your driver</p>
                  </Card>
                )}
                
                {/* Already Tipped Message */}
                {(hasTipped || ride.tip_amount > 0) && (
                  <div className="flex items-center justify-center gap-2 text-green-600 mt-2">
                    <Gift size={16} />
                    <span className="text-sm">You tipped ${ride.tip_amount?.toFixed(2)} - Thank you!</span>
                  </div>
                )}
              </div>
            )}
            
            <Button 
              onClick={() => navigate('/user')}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
            >
              Book Another Ride
            </Button>
          </Card>
        ) : (
          <RideTracker 
            ride={ride} 
            driver={driver} 
            onRefresh={fetchRideDetails}
          />
        )}
      </div>
    </div>
  );
};

export default RideTrackingPage;
